/**
 * GEKHA Homepage Sections — Shared JavaScript
 * Vanilla JS only. No jQuery.
 *
 * Guard pattern: every init function skips elements that already have
 * data-g-init set, so loading this script multiple times (one per section)
 * never double-binds listeners or re-initialises Swiper instances.
 */

(function () {
  'use strict';

  /* ── Tab switching (filterable grids §9 & §13) ──────────────── */
  function initFilterTabs() {
    document.querySelectorAll('.gekha-filter-grid:not([data-g-init])').forEach(function (grid) {
      grid.setAttribute('data-g-init', '1');

      var pills  = grid.querySelectorAll('.gekha-tab-pill');
      var panels = grid.querySelectorAll('.gekha-tab-panel');

      pills.forEach(function (pill) {
        pill.addEventListener('click', function () {
          var target = pill.dataset.tab;

          pills.forEach(function (p) {
            p.classList.remove('is-active');
            p.setAttribute('aria-selected', 'false');
          });
          panels.forEach(function (p) { p.classList.remove('is-active'); });

          pill.classList.add('is-active');
          pill.setAttribute('aria-selected', 'true');

          var panel = grid.querySelector('.gekha-tab-panel[data-tab="' + target + '"]');
          if (panel) panel.classList.add('is-active');
        });
      });

      // Activate first tab by default
      if (pills.length > 0 && panels.length > 0) {
        pills[0].classList.add('is-active');
        pills[0].setAttribute('aria-selected', 'true');
        panels[0].classList.add('is-active');
      }
    });
  }

  /* ── FAQ Accordion (§17) ────────────────────────────────────── */
  function initFaqAccordion() {
    document.querySelectorAll('.gekha-faq-item__trigger:not([data-g-init])').forEach(function (trigger) {
      trigger.setAttribute('data-g-init', '1');

      trigger.addEventListener('click', function () {
        var item    = trigger.closest('.gekha-faq-item');
        var isOpen  = item.classList.contains('is-open');
        var faqList = item.closest('.gekha-faq__list');

        // Close all siblings
        if (faqList) {
          faqList.querySelectorAll('.gekha-faq-item').forEach(function (i) {
            i.classList.remove('is-open');
            var t = i.querySelector('.gekha-faq-item__trigger');
            if (t) t.setAttribute('aria-expanded', 'false');
          });
        }

        // Open current (unless it was already open — click toggles closed)
        if (!isOpen) {
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  /* ── UGC "Read more" toggle (§16) ───────────────────────────── */
  function initReadMore() {
    document.querySelectorAll('.gekha-ugc-card__read-more:not([data-g-init])').forEach(function (btn) {
      btn.setAttribute('data-g-init', '1');

      btn.addEventListener('click', function () {
        var review = btn.previousElementSibling;
        if (!review) return;
        var expanded = review.classList.toggle('is-expanded');
        btn.textContent = expanded ? 'Read less' : 'Read more';
      });
    });
  }

  /* ── Wishlist heart toggle (cosmetic) ───────────────────────── */
  function initWishlist() {
    document.querySelectorAll('.pc-wishlist:not([data-g-init])').forEach(function (btn) {
      btn.setAttribute('data-g-init', '1');

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        btn.classList.toggle('is-wished');
        var wished = btn.classList.contains('is-wished');
        btn.setAttribute('aria-label', wished ? 'Remove from wishlist' : 'Add to wishlist');
        // Dispatch event for wishlist app integration
        document.dispatchEvent(new CustomEvent('gekha:wishlist:toggle', {
          detail: { productId: btn.dataset.productId, wished: wished }
        }));
      });
    });
  }

  /* ── Bottom nav height sync (keeps body padding matched to real height) ── */
  function initBottomNavHeightVar() {
    var nav = document.querySelector('.gekha-bottom-nav:not([data-g-init])');
    if (!nav) return;
    nav.setAttribute('data-g-init', '1');

    function setVar() {
      document.documentElement.style.setProperty('--gekha-bottom-nav-height', nav.offsetHeight + 'px');
    }
    setVar();
    window.addEventListener('resize', setVar);
  }

  /* ── Hero Carousel init ─────────────────────────────────────── */
  function initHeroCarousels() {
    if (typeof Swiper === 'undefined') return;
    document.querySelectorAll('.gekha-hero-swiper:not([data-g-init])').forEach(function (el) {
      el.setAttribute('data-g-init', '1');
      var autoplay = el.dataset.autoplay === 'true';
      var speed    = parseInt(el.dataset.speed, 10) || 5000;
      new Swiper(el, {
        loop: true,
        speed: 600,
        autoplay: autoplay ? { delay: speed, disableOnInteraction: false } : false,
        pagination: {
          el: el.querySelector('.swiper-pagination'),
          clickable: true
        },
        a11y: { enabled: true }
      });
    });
  }

  /* ── Influencer Carousel init ───────────────────────────────── */
  function initInfluencerCarousels() {
    if (typeof Swiper === 'undefined') return;
    document.querySelectorAll('.gekha-influencer-swiper:not([data-g-init])').forEach(function (el) {
      el.setAttribute('data-g-init', '1');
      var wrap = el.closest('.gekha-influencer');
      new Swiper(el, {
        loop: false,
        slidesPerView: 1.4,
        spaceBetween: 10,
        freeMode: true,
        navigation: {
          nextEl: wrap ? wrap.querySelector('.swiper-button-next') : null,
          prevEl: wrap ? wrap.querySelector('.swiper-button-prev') : null
        },
        breakpoints: {
          480:  { slidesPerView: 2.2 },
          750:  { slidesPerView: 3.2, spaceBetween: 14 },
          1100: { slidesPerView: 4.2, spaceBetween: 16 }
        }
      });
    });
  }

  /* ── PDP: guard against "ghost click" auto-opening the zoom modal ──
     Symptom: tapping a product card on a collection page can deliver a
     delayed synthetic click to the PDP's main image link if it lands at the
     same screen coordinates, auto-opening the gallery-viewer zoom modal
     right on arrival. A real tap always has a pointerdown/touchstart on the
     same element immediately before the click; a ghost click doesn't. */
  function initGalleryGhostClickGuard() {
    if (document.documentElement.dataset.gGalleryGuard) return;
    document.documentElement.dataset.gGalleryGuard = '1';

    var navigatedAt = Date.now();
    var sawRealPress = false;

    function markRealPress(evt) {
      if (evt.target.closest && evt.target.closest('.show-gallery')) sawRealPress = true;
    }
    document.addEventListener('pointerdown', markRealPress, true);
    document.addEventListener('touchstart', markRealPress, { capture: true, passive: true });

    document.addEventListener('click', function (evt) {
      var link = evt.target.closest && evt.target.closest('.show-gallery');
      if (!link) return;
      if (!sawRealPress && Date.now() - navigatedAt < 500) {
        evt.stopImmediatePropagation();
        evt.preventDefault();
      }
    }, true);
  }

  /* ── PDP: gallery overlay badges ────────────────────────────── */
  function initPdpOverlays() {
    var dataEl = document.getElementById('gekha-pdp-data');
    if (!dataEl || dataEl.dataset.gInit) return;
    dataEl.dataset.gInit = '1';

    var mainImage = document.querySelector('.media-gallery .main-image');
    if (!mainImage) return;

    var ribbon  = dataEl.dataset.ribbon;
    var rating  = dataEl.dataset.rating  ? parseFloat(dataEl.dataset.rating) : 0;
    var count   = dataEl.dataset.ratingCount || '';
    var type    = dataEl.dataset.type || '';
    var discount = dataEl.dataset.discount ? parseInt(dataEl.dataset.discount, 10) : 0;
    var showTax  = dataEl.dataset.showTax === 'true';
    var taxText  = dataEl.dataset.taxText || 'Inclusive of all taxes';

    /* Build overlay wrapper */
    var root = document.createElement('div');
    root.className = 'gekha-pdp-overlay-root';
    root.setAttribute('aria-hidden', 'true');

    if (ribbon) {
      var rib = document.createElement('span');
      rib.className = 'gekha-pdp-ribbon';
      rib.textContent = ribbon;
      root.appendChild(rib);
    }

    if (rating > 0) {
      var ratingEl = document.createElement('div');
      ratingEl.className = 'gekha-pdp-rating-badge';
      ratingEl.innerHTML =
        '<svg width="11" height="11" viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg" fill="#bf570a">' +
        '<path d="M5 .5 6.18 3.26l3 .44-2.17 2.11.51 2.99L5 7.38 2.48 8.8l.51-3L.82 3.7l3-.44z"/>' +
        '</svg>' +
        '<span>' + rating.toFixed(1) + '</span>' +
        (count ? '<span class="gekha-pdp-rating-count">(' + count + ')</span>' : '');
      root.appendChild(ratingEl);
    }

    if (type) {
      var pill = document.createElement('span');
      pill.className = 'gekha-pdp-category-pill';
      pill.textContent = type.toUpperCase();
      root.appendChild(pill);
    }

    mainImage.appendChild(root);

    /* Discount badge — inject next to price */
    if (discount > 0) {
      var priceRow = document.querySelector('.product-price--large');
      if (priceRow) {
        var badge = document.createElement('span');
        badge.className = 'gekha-pdp-discount-badge';
        badge.textContent = '-' + discount + '%';
        priceRow.appendChild(badge);
      }
    }

    /* Tax note — inject below price container */
    if (showTax) {
      var priceContainer = document.querySelector('.price-container');
      if (priceContainer) {
        var note = document.createElement('span');
        note.className = 'gekha-pdp-tax-note';
        note.textContent = taxText;
        priceContainer.appendChild(note);
      }
    }
  }

  /* ── PDP: sticky bar — size display, variant sync, Buy Now ──── */
  function initPdpStickyBar() {
    var bar = document.getElementById('gekha-pdp-sticky-bar');
    if (!bar || bar.dataset.gInit) return;
    bar.dataset.gInit = '1';

    var stickyInput   = document.getElementById('gekha-sticky-variant-id');
    var buyNowBtn     = document.getElementById('gekha-buy-now-btn');
    var atcBtn        = document.getElementById('gekha-atc-btn');
    var sizeRow       = document.getElementById('gekha-sticky-size-row');
    var sizeValueEl   = document.getElementById('gekha-sticky-size-value');

    /* ── Build a variant lookup map from the page's embedded JSON ─
       Symmetry 8 embeds variants JSON inside <variant-picker> as a
       <script type="application/json"> block, or as a full product JSON.
       We try multiple locations so we always have availability data.       */
    var variantMap = {};

    function buildVariantMap(variants) {
      if (!Array.isArray(variants)) return;
      variants.forEach(function (v) { variantMap[String(v.id)] = v; });
    }

    /* Try 1: variants array inside <variant-picker> */
    var vpJsonEl = document.querySelector('variant-picker script[type="application/json"]');
    if (vpJsonEl) {
      try { buildVariantMap(JSON.parse(vpJsonEl.textContent)); } catch (e) {}
    }

    /* Try 2: full product JSON embedded anywhere on the page */
    if (!Object.keys(variantMap).length) {
      document.querySelectorAll('script[type="application/json"]').forEach(function (el) {
        if (Object.keys(variantMap).length) return;
        try {
          var data = JSON.parse(el.textContent);
          if (data && data.variants) buildVariantMap(data.variants);
          else if (Array.isArray(data) && data[0] && data[0].id && data[0].options) buildVariantMap(data);
        } catch (e) {}
      });
    }

    /* ── Find the main product form and its hidden variant input ── */
    var mainForm  = document.querySelector('form.js-product-form') ||
                    document.querySelector('.js-product-form[data-product-id]');
    var mainInput = mainForm ? mainForm.querySelector('input[name="id"]') : null;

    /* ── Scroll to the variant picker in the main form ──────────── */
    function scrollToSizePicker() {
      var picker = document.querySelector('variant-picker') ||
                   document.querySelector('.option-selector') ||
                   document.querySelector('#product-info');
      if (picker) {
        picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
        var optBtns = document.querySelector('.option-selector__btns');
        if (optBtns) {
          optBtns.style.transition = 'box-shadow 0.2s';
          optBtns.style.boxShadow = '0 0 0 2px #bf570a';
          setTimeout(function () { optBtns.style.boxShadow = ''; }, 1200);
        }
      }
    }

    /* Tapping the size row scrolls to the picker */
    if (sizeRow) {
      sizeRow.addEventListener('click', scrollToSizePicker);
      sizeRow.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') scrollToSizePicker();
      });
    }

    /* ── Apply a variant object to the sticky bar ─────────────── */
    function applyVariant(variant) {
      if (!variant) return;

      /* Update hidden form input */
      if (stickyInput) stickyInput.value = variant.id;

      /* Show variant title in size row */
      if (sizeValueEl) {
        var label = variant.title && variant.title !== 'Default Title'
          ? variant.title
          : 'Selected';
        sizeValueEl.textContent = label;
        if (sizeRow) sizeRow.classList.remove('gekha-sticky-size-row--unselected');
      }

      /* Enable/disable buttons based on availability.
         buyNowBtn now wraps Shopify's native dynamic checkout button
         (rendered via {{ form | payment_button }}), which manages its own
         enabled state from the form's inputs — we just dim the wrapper. */
      var avail = variant.available;
      if (buyNowBtn) {
        buyNowBtn.classList.toggle('is-disabled', !avail);
      }
      if (atcBtn) {
        atcBtn.disabled = !avail;
        atcBtn.textContent = avail ? atcBtn.getAttribute('aria-label') : 'Sold Out';
      }
    }

    /* ── Sync from main form's input[name="id"] ───────────────────
       NOTE: Shopify/Symmetry theme JS sets input.value as a property
       (not an HTML attribute), so MutationObserver on attributes does
       NOT fire. Instead we read the value after a microtask delay.   */
    function syncFromMainForm() {
      if (!mainInput) return;
      var vid = String(mainInput.value);
      if (!vid) return;

      /* Sync sticky hidden input */
      if (stickyInput) stickyInput.value = vid;

      /* Use our variant map for full data (title + availability) */
      var variant = variantMap[vid];
      if (variant) {
        applyVariant(variant);
      } else {
        /* Variant map not populated — at minimum sync the ID.
           Fetch availability from Shopify variants API as fallback. */
        fetch('/variants/' + vid + '.js')
          .then(function (r) { return r.json(); })
          .then(function (v) { variantMap[vid] = v; applyVariant(v); })
          .catch(function () {
            /* Last resort: just update the variant ID, keep buttons enabled */
            if (stickyInput) stickyInput.value = vid;
          });
      }
    }

    /* ── Event sources that indicate a variant change ─────────────
       We listen to ALL of these because different Shopify theme versions
       fire different events, and we want zero misses.                */

    /* Source 1: Symmetry 8 / OS2 themes dispatch 'variant:change' */
    document.addEventListener('variant:change', function (e) {
      if (e.detail && e.detail.variant) applyVariant(e.detail.variant);
    });

    /* Source 2: Some themes dispatch 'theme:variant:update' */
    document.addEventListener('theme:variant:update', function (e) {
      if (e.detail && e.detail.variant) applyVariant(e.detail.variant);
    });

    /* Source 3 (PRIMARY FIX): change events on option radio inputs.
       Fires whenever any option (color OR size) is clicked.
       We use requestAnimationFrame so Symmetry's own JS runs first
       and updates input[name="id"] before we read it.               */
    document.addEventListener('change', function (e) {
      var el = e.target;
      if (!el) return;
      var inVariantPicker = el.closest('variant-picker') ||
                            el.closest('.option-selector') ||
                            (el.classList && el.classList.contains('opt-btn'));
      if (!inVariantPicker) return;

      /* One rAF is usually enough; add a 0ms timeout as belt-and-suspenders */
      requestAnimationFrame(function () {
        setTimeout(syncFromMainForm, 0);
      });
    });

    /* Source 4: URL changes — Symmetry updates ?variant=ID in the URL */
    window.addEventListener('popstate', function () {
      requestAnimationFrame(syncFromMainForm);
    });

    /* Mark size row as "unselected" if no real variant chosen yet */
    if (sizeRow && sizeValueEl && sizeValueEl.textContent.trim() === 'Select size') {
      sizeRow.classList.add('gekha-sticky-size-row--unselected');
    }

    /* "Buy Now" now wraps Shopify's native dynamic checkout button, which
       submits the form itself (isolated checkout for just this variant —
       doesn't touch the existing cart). We only need to guard against
       submitting with no size chosen yet, intercepted in the capture phase
       so it runs before the native button's own click handler. */
    if (buyNowBtn) {
      buyNowBtn.addEventListener('click', function (e) {
        if (sizeValueEl && sizeValueEl.textContent.trim() === 'Select size') {
          e.preventDefault();
          e.stopPropagation();
          scrollToSizePicker();
        }
      }, true);
    }

    /* "Add to Cart" submit — prompt size if unselected */
    if (atcBtn) {
      atcBtn.addEventListener('click', function (e) {
        if (sizeValueEl && sizeValueEl.textContent.trim() === 'Select size') {
          e.preventDefault();
          scrollToSizePicker();
        }
      });
    }

    /* Auto-hide sticky bar when the inline buy button is in viewport */
    var inlineAtc = document.querySelector('.buy-buttons-row');
    if (inlineAtc && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          bar.classList.toggle('is-hidden', entry.isIntersecting);
        });
      }, { threshold: 0.5 }).observe(inlineAtc);
    }
  }

  /* ── PDP: Fabric Features slider (2 slides at a time) ──────── */
  function initFabricFeaturesSliders() {
    if (typeof Swiper === 'undefined') return;
    document.querySelectorAll('.gekha-fabric-swiper:not([data-g-init])').forEach(function (el) {
      el.setAttribute('data-g-init', '1');
      new Swiper(el, {
        loop: false,
        slidesPerView: 2,
        spaceBetween: 12,
        breakpoints: {
          750: {
            slidesPerView: 3,
            spaceBetween: 16
          }
        },
        pagination: {
          el: el.querySelector('.gekha-fabric-pagination'),
          clickable: true
        },
        a11y: { enabled: true }
      });
    });
  }

  /* ── Shared: horizontal scroller with arrow nav + autoplay ───────
     Prev/next arrow buttons (if present) scroll the row via click, each
     disabling itself at its corresponding end. Native touch swipe /
     trackpad horizontal scroll on the row itself is unaffected. Also
     auto-advances on a timer, looping back to the start at the end;
     paused while the user is interacting (hover, touch, or the arrow
     buttons) and resumes a few seconds after they stop. Used by the PDP
     customer-stories row and the related-products slider. */
  function setupHorizontalScroller(grid, navWrapSelector, prevSelector, nextSelector, itemSelector) {
    var navWrap = grid.closest(navWrapSelector);
    if (!navWrap) return;

    var prevBtn = navWrap.querySelector(prevSelector);
    var nextBtn = navWrap.querySelector(nextSelector);
    if (!prevBtn || !nextBtn) return;

    var gap = parseFloat(getComputedStyle(grid).columnGap) || 12;

    var scrollByAmount = function () {
      var item = grid.querySelector(itemSelector);
      return item ? item.getBoundingClientRect().width + gap : grid.clientWidth * 0.9;
    };

    var updateButtons = function () {
      var atStart = grid.scrollLeft <= 0;
      var atEnd = grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 1;
      prevBtn.disabled = atStart;
      nextBtn.disabled = atEnd;
    };

    prevBtn.addEventListener('click', function () {
      grid.scrollBy({ left: -scrollByAmount(), behavior: 'smooth' });
      pauseThenResumeAutoScroll();
    });
    nextBtn.addEventListener('click', function () {
      grid.scrollBy({ left: scrollByAmount(), behavior: 'smooth' });
      pauseThenResumeAutoScroll();
    });
    grid.addEventListener('scroll', updateButtons, { passive: true });
    updateButtons();

    var AUTO_SCROLL_INTERVAL = 3500;
    var AUTO_SCROLL_RESUME_DELAY = 5000;
    var autoScrollTimer = null;
    var resumeTimer = null;
    var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function stopAutoScroll() {
      if (autoScrollTimer) clearInterval(autoScrollTimer);
      autoScrollTimer = null;
    }

    function startAutoScroll() {
      stopAutoScroll();
      autoScrollTimer = setInterval(function () {
        var atEnd = grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 1;
        if (atEnd) {
          grid.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          grid.scrollBy({ left: scrollByAmount(), behavior: 'smooth' });
        }
      }, AUTO_SCROLL_INTERVAL);
    }

    function pauseThenResumeAutoScroll() {
      stopAutoScroll();
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(startAutoScroll, AUTO_SCROLL_RESUME_DELAY);
    }

    if (!reducedMotion) {
      grid.addEventListener('mouseenter', stopAutoScroll);
      grid.addEventListener('mouseleave', startAutoScroll);
      grid.addEventListener('pointerdown', pauseThenResumeAutoScroll);
      startAutoScroll();
    }
  }

  /* ── PDP: Customer Stories — scroll + arrow nav ─────────────────── */
  function initCustomerStoriesScroll() {
    document.querySelectorAll('.pdp-customer-stories__grid:not([data-g-init])').forEach(function (grid) {
      grid.setAttribute('data-g-init', '1');
      setupHorizontalScroller(grid, '.pdp-customer-stories__nav-wrap', '.pdp-cs-nav--prev', '.pdp-cs-nav--next', '.pdp-cs-card');
    });
  }

  /* ── PDP: Related products ("You may also like") slider ────────── */
  function initRelatedProductsSlider() {
    document.querySelectorAll('.gekha-related-products__grid:not([data-g-init])').forEach(function (grid) {
      grid.setAttribute('data-g-init', '1');
      setupHorizontalScroller(grid, '.gekha-related-products__nav-wrap', '.gekha-related-products__nav--prev', '.gekha-related-products__nav--next', '.pc-card');
    });
  }

  /* ── PDP: Hidden Gems size filter ───────────────────────────── */
  function initHiddenGems() {
    document.querySelectorAll('.gekha-hidden-gems:not([data-g-init])').forEach(function (section) {
      section.setAttribute('data-g-init', '1');

      var pills = section.querySelectorAll('.gekha-hidden-gems__size-pill');
      var cards = section.querySelectorAll('.gekha-hidden-gems__card-wrap');

      pills.forEach(function (pill) {
        pill.addEventListener('click', function () {
          var size = pill.dataset.size;

          pills.forEach(function (p) { p.classList.remove('is-active'); });
          pill.classList.add('is-active');

          cards.forEach(function (card) {
            if (size === 'all') {
              card.classList.remove('is-hidden');
            } else {
              var sizes = (card.dataset.sizes || '').toLowerCase().split(',');
              card.classList.toggle('is-hidden', !sizes.includes(size.toLowerCase()));
            }
          });
        });
      });
    });
  }

  /* ── Collection: Aramya-style filter bar ───────────────────── */
  function initCollectionFilterBar() {
    if (!document.body.classList.contains('template-collection')) return;

    var SORT_ICON = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="5" x2="21" y2="5"/><line x1="3" y1="10" x2="14" y2="10"/><line x1="3" y1="15" x2="9" y2="15"/><polyline points="17 13 21 17 17 21"/></svg>';
    var SIZE_ICON  = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 3H3a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z"/><line x1="7" y1="8" x2="7" y2="16"/><line x1="12" y1="8" x2="12" y2="13"/><line x1="17" y1="8" x2="17" y2="16"/></svg>';

    function injectIntoBar(bar) {
      if (!bar) return;

      // 1. Replace sort button content with "Sort By" label
      var sortBtn = bar.querySelector('.link-dropdown__button');
      if (sortBtn && !sortBtn.querySelector('.gekha-sort-label')) {
        var label = document.createElement('span');
        label.className = 'gekha-sort-label';
        label.innerHTML = SORT_ICON + '<span>Sort By</span>';
        sortBtn.insertBefore(label, sortBtn.firstChild);
      }

      // 2. Inject Size button (only once per bar)
      if (!bar.querySelector('.utility-bar__size-btn')) {
        var sizeBtn = document.createElement('button');
        sizeBtn.type = 'button';
        sizeBtn.className = 'utility-bar__size-btn';
        sizeBtn.setAttribute('aria-label', 'Filter by size');
        sizeBtn.innerHTML = SIZE_ICON + '<span>Size</span>';
        bar.appendChild(sizeBtn);

        sizeBtn.addEventListener('click', function () {
          // Open the filter panel
          var filterToggle = document.querySelector('[data-toggle-filters]');
          if (filterToggle) filterToggle.click();
          // After panel slides in, scroll to Size filter group
          setTimeout(function () {
            var groups = document.querySelectorAll('.filter-group__heading__text');
            for (var i = 0; i < groups.length; i++) {
              if (groups[i].textContent.trim().toLowerCase().includes('size')) {
                groups[i].closest('.filter-group').scrollIntoView({ behavior: 'smooth', block: 'start' });
                break;
              }
            }
          }, 350);
        });
      }
    }

    // Inject into the main (non-sticky) utility bar
    var mainBar = document.querySelector('.utility-bar:not(.utility-bar--sticky-mobile-copy)');
    injectIntoBar(mainBar);

    // Symmetry creates the sticky copy via JS shortly after page load
    setTimeout(function () {
      var stickyBar = document.querySelector('.utility-bar--sticky-mobile-copy');
      injectIntoBar(stickyBar);
    }, 600);
  }

  /* ── Init all ───────────────────────────────────────────────── */
  function init() {
    initFilterTabs();
    initFaqAccordion();
    initReadMore();
    initWishlist();
    initBottomNavHeightVar();
    initHeroCarousels();
    initInfluencerCarousels();
    initGalleryGhostClickGuard();
    initPdpOverlays();
    initPdpStickyBar();
    initCustomerStoriesScroll();
    initRelatedProductsSlider();
    initHiddenGems();
    initFabricFeaturesSliders();
    initCollectionFilterBar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init after Shopify theme-editor section events.
  // The data-g-init guards ensure only newly inserted elements are touched.
  document.addEventListener('shopify:section:load', init);

  // Related products are fetched via AJAX after initial page load (see
  // gekha-related-products.liquid), so the slider markup doesn't exist yet
  // when the listener above runs at DOMContentLoaded.
  document.addEventListener('gekha:related-products:loaded', initRelatedProductsSlider);
})();
